import {
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';

import { Store, select } from '@ngrx/store';
import { Observable, Subject, race, timer } from 'rxjs';
import { catchError, filter, first, takeUntil, tap } from 'rxjs/operators';

import { TWorkspacesById } from 'src/app/project/modules/workspace/workspace.model';

import { CustomFieldsService } from 'src/app/project/modules/custom-fields/custom-fields.service';
import { WorkspaceService } from 'src/app/project/modules/workspace/workspace.service';
import { ActiveService } from 'src/app/project/services/active/active.service';
import { SiteFilterDropdownService } from './site-filter-dropdown-service/site-filter-dropdown.service';

import { ClickOutsideHandler } from '@core/services';
import { ModalService } from 'src/app/project/components/modal/modal.service';
import { checkCustomWorkspaceId } from 'src/app/project/modules/workspace/workspace';
import { logEventInGTAG } from 'src/app/project/services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventSite,
} from 'src/app/project/services/analytics/google-analytics.consts';
import { EIconPath } from '../../../shared/enums/icons.enum';
import { EStore } from '../../../shared/enums/store.enum';
import { ResponseErrorService } from '../../errors/response-error.service';
import { SavedViewsService } from '../../saved-views/saved-views.service';
import { ResetTableService } from '../../site/reset-table.service';
import { TableLoadingService } from '../../site/site-table/custom-table/table-loading.service';
import { QuickFilterService } from '../filters-api/search/quick-filter-service';
import { TFilters as TQuickFilters } from '../filters-api/search/quick-filter-service.consts';
import { countActiveQuickFilters } from '../utils/count-active-quick-filters';
import { siteFilterClickOutside } from './site-filter-dropdown-utils/site-filter-click-outside';

const FILTER_DROPDOWN_MIN_WIDTH = '41.75rem';

@Component({
  selector: 'pp-site-filter-dropdown',
  templateUrl: './site-filter-dropdown.component.html',
  styleUrls: ['./site-filter-dropdown.component.scss'],
  standalone: false,
})
export class SiteFilterDropdownComponent implements OnInit, OnDestroy {
  @Input() ppDataFetched = true;
  @Input() ppHideTextOnMobile = true;
  customFields = this.customFieldsService.getCustomFields();
  isDropdownVisible: boolean;
  EIconPath = EIconPath;
  saveViewProcessing = false;
  minWidth = FILTER_DROPDOWN_MIN_WIDTH;

  private readonly destroy$ = new Subject<void>();

  private clickOutsideHandler: ClickOutsideHandler;

  private workspaces$ = new Observable<TWorkspacesById>();
  workspaceId: string;
  filterNumber = '';
  shouldDisplaySaveToCurrentView: boolean;
  quickFilters: TQuickFilters;
  isFiltersLoading: boolean;

  constructor(
    private store: Store<{
      workspaces: TWorkspacesById;
    }>,
    private customFieldsService: CustomFieldsService,
    private workspaceService: WorkspaceService,
    private activeService: ActiveService,
    private siteFilterDropdownService: SiteFilterDropdownService,
    private savedViewsService: SavedViewsService,
    private responseErrorService: ResponseErrorService,
    private resetTableService: ResetTableService,
    private quickFilterService: QuickFilterService,
    private cdr: ChangeDetectorRef,
    private tableLoadingService: TableLoadingService,
    private modalService: ModalService,
  ) {
    this.workspaces$ = this.store.pipe(select(EStore.WORKSPACES));

    this.workspaces$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.workspacesHandler();
    });

    this.quickFilters = this.quickFilterService.getFilters();

    this.quickFilterService
      .getFiltersChange$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterNumber = this.getNumberOfQuickFilters();
      });
  }

  @HostListener('document:keydown.esc')
  onEscPress(): void {
    if (this.isDropdownVisible) {
      this.hideDropdown(true);
    }
  }

  ngOnInit() {
    this.shouldDisplaySaveToCurrentView = !window.location.href.includes('timeline');

    this.isDropdownVisible = false;
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  onFilterDropdownRendered(filterDropdownElement: HTMLElement): void {
    this.clickOutsideHandler = new ClickOutsideHandler(filterDropdownElement, this.destroy$, {
      mouseEventTypes: 'mousedown',
    });
    this.clickOutsideHandler.caught$.subscribe((event) => {
      this.onClickOutside(event);
    });
  }

  toggleDropdown(): void {
    if (this.isDropdownVisible) {
      this.hideDropdown(true);
    } else {
      this.showDropdown();
    }
  }

  handleApplyFilters(): void {
    this.isFiltersLoading = true;
    this.cdr.detectChanges();

    race(this.tableLoadingService.isTableLoading$.pipe(filter((loading) => loading)), timer(1000))
      .pipe(first())
      .subscribe(() => {
        this.hideDropdown(false);
        this.cdr.detectChanges();
      });
  }

  hideDropdown(cancel: boolean = false): void {
    if (cancel) {
      this.isFiltersLoading = false;
    }
    this.isDropdownVisible = false;

    this.clickOutsideHandler.disable();
  }

  clearFilters(): void {
    this.isFiltersLoading = true;
    logEventInGTAG(EGoogleEventSite.SITE__FILTER__CLEAR_ALL, {
      source: EGoogleEventCategory.SITE,
    });

    this.resetTableService.resetFilters();
    this.hideDropdown();
  }

  showDropdown(): void {
    this.isFiltersLoading = false;
    this.isDropdownVisible = true;
    this.customFields = this.siteFilterDropdownService.getCustomFields(
      this.workspaceService.getActiveWorkspace(),
    );

    this.clickOutsideHandler.enable();
  }

  private workspacesHandler(): void {
    this.workspaceId = checkCustomWorkspaceId() || this.activeService.getActiveWorkspaceId();

    this.customFields = this.customFieldsService.getCustomFields();
  }

  private onClickOutside(event: MouseEvent): void {
    const shouldHideDropdown = siteFilterClickOutside(event);
    const isFocused = this.siteFilterDropdownService.isFocused();
    const isModalOpened = this.modalService.getModal().visible;

    if (shouldHideDropdown && !isFocused && !isModalOpened) {
      event.preventDefault();
      event.stopPropagation();

      this.hideDropdown(true);
    }
  }

  saveFiltersToView(): void {
    const viewId = this.savedViewsService.getSelectedViewId(this.workspaceId);
    const view = this.savedViewsService.getView(viewId);

    this.saveViewProcessing = true;

    view.quickFilters = {
      quick: this.quickFilters.quick,
      advanced: {
        filters: this.quickFilters.advanced,
        operator: this.quickFilters.operator,
      },
    };

    this.savedViewsService
      .saveToView(view, this.workspaceId)
      .pipe(
        tap(() => {
          this.saveViewProcessing = false;
          this.cdr.markForCheck();
        }),
        catchError((error) => {
          this.saveViewProcessing = false;
          this.cdr.markForCheck();
          return this.responseErrorService.handleRequestError(
            error,
            'Failed to save filters to view',
          );
        }),
      )
      .subscribe();
  }

  setQuickFilters(filters: TQuickFilters): void {
    this.quickFilters = filters;

    requestAnimationFrame(() => {
      this.filterNumber = this.getNumberOfQuickFilters();
    });
  }

  private getNumberOfQuickFilters(): string {
    const count = countActiveQuickFilters(this.quickFilterService.getFilters());

    return count ? ' / ' + count.toString() : '';
  }
}
